/**
 * SeansLatestFeed — Public, read-only strip of Sean's recent posts.
 * Displays on the homepage so anything Sean publishes from the Studio
 * is instantly visible to Sciencers.
 */
import React, { useEffect, useState } from "react";
import { seanListPosts, mediaSrc } from "@/lib/api";

const extractYouTubeId = (url) => {
  if (!url) return null;
  const m =
    url.match(/[?&]v=([^&]+)/) ||
    url.match(/youtu\.be\/([^?&]+)/) ||
    url.match(/\/shorts\/([^?&]+)/) ||
    url.match(/\/embed\/([^?&]+)/);
  return m ? m[1] : null;
};

const detectKind = (url) => {
  if (!url) return null;
  if (/youtu\.be\/|youtube\.com\//i.test(url)) return "youtube";
  if (/instagram\.com\//i.test(url)) return "instagram";
  if (/tiktok\.com\//i.test(url)) return "tiktok";
  return "link";
};

export default function SeansLatestFeed() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = () => seanListPosts(10)
      .then((d) => { if (alive) setPosts(d.items || []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    load();
    const id = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (loading) return null;
  if (!posts.length) return null;

  return (
    <section
      id="sean-latest"
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 mt-12"
      data-testid="sean-latest-feed"
    >
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="eyebrow text-[var(--orange)]">📣 straight from sean</div>
          <h2 className="font-display text-3xl sm:text-4xl text-[var(--ink)]">Fresh From The Lab</h2>
          <p className="text-[var(--ink-soft)] mt-1 max-w-xl text-sm">
            Posted by Sean himself. New drops land here first — then everywhere else.
          </p>
        </div>
        <div className="pill" style={{ background: "var(--red)", color: "#fff" }}>
          🔴 LIVE · {posts.length} {posts.length === 1 ? "post" : "posts"}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {posts.map((p) => <PostCard key={p.id} post={p} />)}
      </div>
    </section>
  );
}

function PostCard({ post }) {
  const kind = detectKind(post.media_url);
  const ytId = kind === "youtube" ? extractYouTubeId(post.media_url) : null;

  return (
    <article
      id={`post-${post.id}`}
      className="card-flat bg-white flex flex-col"
      style={{ border: "2.5px solid var(--ink)", boxShadow: "4px 4px 0 0 var(--ink)" }}
      data-testid={`sean-post-${post.id}`}
    >
      {post.upload_url && (
        <div className="rounded-xl overflow-hidden mb-3" style={{ border: "2.5px solid var(--ink)", background: "#0e1230" }}>
          {post.upload_kind === "video" ? (
            <video src={mediaSrc(post.upload_url)} controls playsInline preload="metadata" className="w-full block" style={{ maxHeight: 460 }} data-testid={`sean-post-video-${post.id}`} />
          ) : (
            <img src={mediaSrc(post.upload_url)} alt={post.caption.slice(0, 80)} className="w-full block" style={{ maxHeight: 460, objectFit: "cover" }} data-testid={`sean-post-image-${post.id}`} />
          )}
        </div>
      )}

      {ytId && (
        <div className="rounded-xl overflow-hidden mb-3" style={{ border: "2.5px solid var(--ink)", aspectRatio: "16 / 9" }}>
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${ytId}?rel=0&modestbranding=1&playsinline=1`}
            title={post.caption.slice(0, 60)}
            className="w-full h-full"
            style={{ border: 0 }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      )}

      <div className="flex items-center gap-2 text-[0.7rem] font-mono text-[var(--ink-soft)] mb-2">
        <span className="pill text-[0.6rem]" style={{ background: "var(--orange)", color: "var(--ink)" }}>SEAN</span>
        <span>{timeAgo(post.created_at)}</span>
      </div>

      <p className="text-sm text-[var(--ink)] whitespace-pre-wrap leading-relaxed">{post.caption}</p>

      {post.media_url && !ytId && (
        <a
          href={post.media_url}
          target="_blank"
          rel="noreferrer noopener"
          className="mt-3 inline-flex items-center gap-2 text-sm font-bold text-[var(--red)] hover:underline break-all"
        >
          {kind === "instagram" ? "📸 Watch on Instagram" : kind === "tiktok" ? "🎵 Watch on TikTok" : "🔗 Open link"} ↗
        </a>
      )}

      {post.link_url && (
        <a
          href={post.link_url}
          target="_blank"
          rel="noreferrer noopener"
          className="mt-2 inline-flex items-center gap-2 text-xs font-bold text-[var(--red)] hover:underline break-all"
        >
          🔗 {post.link_url}
        </a>
      )}

      {post.tags?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {post.tags.map((t) => <span key={t} className="pill text-[0.6rem]">#{t}</span>)}
        </div>
      )}
    </article>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}
